import React, { useState, useEffect, useCallback } from "react";
import { debounce } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { Button, Form, Input, Modal, Switch, Space, Tag } from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined, EyeOutlined } from "@ant-design/icons";
import DOMPurify from "dompurify";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import Table from "../../../core/common/dataTable/index";
import Toasts from "../../../core/common/toast";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import WYSIWYGEditor from "../../../core/common/textEditor/QuillWYSIWYG";
import {
  getEmailTemplates,
  createEmailTemplate,
  deleteEmailTemplate,
  resetAll,
} from "../../../core/data/redux/emailTemplatesSlice";

import { isSupportViewer } from "../../../utils/permissionUtils";

const routes = all_routes;

interface EmailTemplateData {
  id: string;
  title: string;
  subject: string;
  fromEmailId: string;
  ccEmailId: string;
  bccEmailId: string;
  msg: string;
  status: boolean;
  created_at?: string;
  updated_at?: string;
}

const EmailTemplates: React.FC = () => {
  const dispatch = useDispatch();
  const emailTemplatesState = useSelector((state: any) => state.emailTemplatesState);

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [viewingTemplate, setViewingTemplate] = useState<EmailTemplateData | null>(null);
  const [showUpdateToast, setShowUpdateToast] = useState(false);
  const [showDeleteToast, setShowDeleteToast] = useState(false);
  const [showFormErrorToast, setShowFormErrorToast] = useState(false);
  const [showCreateSuccessToast, setShowCreateSuccessToast] = useState(false);
  const [updateMessage, setUpdateMessage] = useState("");
  const [deleteMessage, setDeleteMessage] = useState("");
  const [formErrorMessage, setFormErrorMessage] = useState("");
  const [createSuccessMessage, setCreateSuccessMessage] = useState("");
  const [formValues, setFormValues] = useState({
    subject: '',
    msg: ''
  });
  const [searchText, setSearchText] = useState("");

  // Function to extract variables from text
  const extractVariables = (subject: string = '', msg: string = '') => {
    const combinedText = `${subject} ${msg}`;
    const variableMatches = combinedText.match(/(\{[^}]+\}|\[[^\]]+\]|\{\{[^}]+\}\})/g);
    if (!variableMatches) return [];

    const uniqueVariables: string[] = [];
    variableMatches.forEach(item => {
      if (!uniqueVariables.includes(item)) {
        uniqueVariables.push(item);
      }
    });
    return uniqueVariables;
  };
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const templatesData = emailTemplatesState?.templatesData || null;
  const createData = emailTemplatesState?.createData || null;
  const deleteData = emailTemplatesState?.deleteData || null;

  const fetchData = (page = pagination.current, limit = pagination.pageSize, query = searchText) => {
    dispatch(getEmailTemplates({
      page,
      limit,
      query,
    }));
  };

  const debouncedFetch = useCallback(
    debounce((p: any, q: string) => {
      fetchData(p.current, p.pageSize, q);
    }, 500),
    []
  );

  const handleSearch = (value: string) => {
    setSearchText(value);
    const resetPagination = { ...pagination, current: 1 };
    setPagination(resetPagination);

    if (value === "") {
      debouncedFetch.cancel();
      fetchData(1, pagination.pageSize, "");
    } else {
      debouncedFetch(resetPagination, value);
    }
  };

  useEffect(() => {
    fetchData();
  }, [pagination.pageSize]);

  useEffect(() => {
    if (emailTemplatesState.action === "create" && emailTemplatesState.loading === "fulfilled") {
      setCreateSuccessMessage(createData?.message || "Email template created/updated successfully!");
      setShowCreateSuccessToast(true);
      setTimeout(() => {
        setShowCreateSuccessToast(false);
        dispatch(resetAll());
      }, 4000);
      setIsModalVisible(false);
      form.resetFields();
      setEditingId(null);
      fetchData();
    }
  }, [emailTemplatesState.action, emailTemplatesState.loading, createData]);

  useEffect(() => {
    if (emailTemplatesState.action === "delete" && emailTemplatesState.loading === "fulfilled") {
      setDeleteMessage(deleteData?.message || "Email template deleted successfully!");
      setShowDeleteToast(true);
      setTimeout(() => {
        setShowDeleteToast(false);
        dispatch(resetAll());
      }, 3000);
      fetchData();
    }
  }, [emailTemplatesState.action, emailTemplatesState.loading, deleteData]);

  useEffect(() => {
    try {
      if (templatesData) {
        let total = 0;
        let currentPage = pagination.current;
        let pageSize = pagination.pageSize;

        // Try different response structures for pagination info
        if (templatesData.data?.total !== undefined) {
          total = templatesData.data.total;
          currentPage = templatesData.data.page || pagination.current;
          pageSize = templatesData.data.limit || pagination.pageSize;
        } else if (templatesData.data?.data?.total !== undefined) {
          total = templatesData.data.data.total;
          currentPage = templatesData.data.data.page || pagination.current;
          pageSize = templatesData.data.data.limit || pagination.pageSize;
        } else {
          // Fallback: count array length
          const dataArray = getTableData();
          total = dataArray.length;
        }

        setPagination((prev) => ({
          current: currentPage,
          pageSize: pageSize,
          total: total,
        }));
      }
    } catch (error) {
      // Handle pagination error silently
    }
  }, [templatesData]);

  // Watch form values for variable extraction
  useEffect(() => {
    const interval = setInterval(() => {
      const watchFields = form.getFieldsValue(['subject', 'msg']);
      setFormValues(watchFields);
    }, 500);

    return () => clearInterval(interval);
  }, [form]);

  const columns = [
    {
      title: "Sr. No.",
      dataIndex: "id",
      render: (_: any, __: any, index: number) => (
        <div>{(pagination.current - 1) * pagination.pageSize + index + 1}</div>
      ),
      width: 80,
    },
    {
      title: "Template Title",
      dataIndex: "title",
      render: (title: string) => (
        <div style={{ fontWeight: 500, color: "#1890ff" }}>
          {title || "-"}
        </div>
      ),
      sorter: (a: EmailTemplateData, b: EmailTemplateData) =>
        (a.title || "").localeCompare(b.title || ""),
    },
    {
      title: "Subject",
      dataIndex: "subject",
      render: (subject: string) => (
        <div style={{ maxWidth: "200px", wordWrap: "break-word" }}>
          {subject || "-"}
        </div>
      ),
      sorter: (a: EmailTemplateData, b: EmailTemplateData) =>
        (a.subject || "").localeCompare(b.subject || ""),
    },
    {
      title: "From Email",
      dataIndex: "fromEmailId",
      render: (email: string) => (
        <div style={{ fontSize: "12px", color: "#666" }}>
          {email || "-"}
        </div>
      ),
    },
    {
      title: "Message Preview",
      dataIndex: "msg",
      render: (msg: string) => (
        <div style={{
          maxWidth: "400px",
          wordWrap: "break-word",
          overflow: "hidden",
          display: "-webkit-box",
          WebkitLineClamp: 3,
          WebkitBoxOrient: "vertical",
          lineHeight: "1.4"
        }}>
          <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(msg || "-") }} />
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status: boolean) => (
        <Tag color={status ? "success" : "error"} style={{ fontWeight: 500 }}>
          {status ? "Active" : "Inactive"}
        </Tag>
      ),
      width: 100,
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      render: (date: string) => (
        <div style={{ fontSize: "12px" }}>
          {date
            ? new Date(date).toLocaleDateString("en-IN", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
            })
            : "N/A"}
        </div>
      ),
      width: 120,
    },
    {
      title: "Actions",
      dataIndex: "actions",
      render: (_: any, record: EmailTemplateData) => (
        <Space>
          <Button
            icon={<EyeOutlined />}
            onClick={() => handleView(record)}
            size="small"
            title="View Template"
          />
          {!isSupportViewer() && (
            <>
              <Button
                icon={<EditOutlined />}
                onClick={() => handleEdit(record)}
                size="small"
                title="Edit Template"
              />
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(record.id)}
                size="small"
                title="Delete Template"
              />
            </>
          )}
        </Space>
      ),
      width: 120,
    },
  ];

  const handleAdd = () => {
    setEditingId(null);
    form.resetFields();
    form.setFieldsValue({
      status: true
    });
    setFormValues({ subject: '', msg: '' });
    setIsModalVisible(true);
  };

  const handleEdit = (record: EmailTemplateData) => {
    setEditingId(record.id);
    form.setFieldsValue({
      title: record.title,
      subject: record.subject,
      fromEmailId: record.fromEmailId,
      ccEmailId: record.ccEmailId,
      bccEmailId: record.bccEmailId,
      msg: record.msg,
      status: record.status,
    });
    setFormValues({
      subject: record.subject || '',
      msg: record.msg || ''
    });
    setIsModalVisible(true);
  };

  const handleView = (record: EmailTemplateData) => {
    setViewingTemplate(record);
    setIsViewModalVisible(true);
  };

  const handleDelete = (id: string | number) => {
    if (!id) {
      return;
    }
    setDeletingId(String(id));
    setIsDeleteModalVisible(true);
  };

  const confirmDelete = () => {
    if (deletingId) {
      dispatch(deleteEmailTemplate({ id: deletingId }));
      setIsDeleteModalVisible(false);
      setDeletingId(null);
    }
  };

  const cancelDelete = () => {
    setIsDeleteModalVisible(false);
    setDeletingId(null);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();

      const payload = {
        ...(editingId && { id: editingId }),
        title: values.title,
        subject: values.subject,
        fromEmailId: values.fromEmailId,
        ccEmailId: values.ccEmailId,
        bccEmailId: values.bccEmailId,
        msg: values.msg,
        status: values.status !== undefined ? values.status : true,
      };

      dispatch(createEmailTemplate(payload));
    } catch (error) {
      setFormErrorMessage("Please check all required fields and try again.");
      setShowFormErrorToast(true);
      setTimeout(() => setShowFormErrorToast(false), 3000);
    }
  };

  const handlePagination = (paginationConfig: any) => {
    setPagination({
      current: paginationConfig.current,
      pageSize: paginationConfig.pageSize,
      total: paginationConfig.total,
    });
    fetchData(paginationConfig.current, paginationConfig.pageSize, searchText);
  };

  const getTableData = () => {
    // Try multiple possible response structures
    if (templatesData?.data?.data?.data && Array.isArray(templatesData.data.data.data)) {
      return templatesData.data.data.data;
    } else if (templatesData?.data?.data && Array.isArray(templatesData.data.data)) {
      return templatesData.data.data;
    } else if (templatesData?.data && Array.isArray(templatesData.data)) {
      return templatesData.data;
    } else if (templatesData?.result && Array.isArray(templatesData.result)) {
      return templatesData.result;
    } else if (Array.isArray(templatesData)) {
      return templatesData;
    }

    return [];
  };

  const tableData = getTableData();

  return (
    <div className="page-wrapper">
      <Toasts
        show={emailTemplatesState.loading === "rejected"}
        message={emailTemplatesState.error}
        title={"Failed"}
        type={"danger"}
      />
      <Toasts
        show={showCreateSuccessToast}
        message={createSuccessMessage}
        title={"Success"}
        type={"success"}
      />
      <Toasts
        show={showDeleteToast}
        message={deleteMessage}
        title={"Success"}
        type={"success"}
      />
      <Toasts
        show={showFormErrorToast}
        message={formErrorMessage}
        title={"Validation Error"}
        type={"danger"}
      />

      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
          <div className="my-auto mb-2">
            <h2 className="mb-1">Email Templates</h2>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>
                    <i className="ti ti-smart-home" />
                  </Link>
                </li>
                <li className="breadcrumb-item">Templates</li>
                <li className="breadcrumb-item active" aria-current="page">
                  Email Templates
                </li>
              </ol>
            </nav>
          </div>
          <div className="head-icons ms-2 d-flex align-items-center gap-2">
            {!isSupportViewer() && (
              <Button
                type="primary"
                onClick={handleAdd}
                icon={<PlusOutlined />}
                style={{
                  backgroundColor: "#c0282c",
                  border: "1px solid #c0282c",
                  fontWeight: "500",
                }}
              >
                Add Email Template
              </Button>
            )}

            <CollapseHeader />
          </div>
        </div>

        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap">
            <h5 className="card-title mb-0">
              <i className="ti ti-mail me-2" />
              Email Templates ({pagination.total} total)
            </h5>
          </div>
          <div className="card-body p-0 py-3">
            {emailTemplatesState.loading === "rejected" && (
              <div style={{ textAlign: "center", padding: "20px", color: "#ff4d4f" }}>
                <i className="ti ti-alert-circle" style={{ fontSize: "24px", marginRight: "8px" }} />
                <span>Failed to load email templates: {emailTemplatesState.error}</span>
                <Button type="link" onClick={() => fetchData()}>Try Again</Button>
              </div>
            )}
            <Table
              columns={columns}
              dataSource={tableData}
              Selection={false}
              paginationConfig={pagination}
              handlePagination={handlePagination}
              loading={emailTemplatesState.loading === "pending"}
              handleSearch={handleSearch}
              searchValue={searchText}
            />
          </div>
        </div>

        {/* Create/Edit Modal */}
        <Modal
          title={
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <i className={`ti ti-${editingId ? "edit" : "plus"}`} />
              {editingId ? "Edit Email Template" : "Add Email Template"}
            </div>
          }
          open={isModalVisible}
          onOk={handleModalOk}
          onCancel={() => {
            setIsModalVisible(false);
            form.resetFields();
            setEditingId(null);
            setFormValues({ subject: '', msg: '' });
          }}
          confirmLoading={emailTemplatesState.loading === "pending" && emailTemplatesState.action === "create"}
          width={700}
          okText={editingId ? "Update" : "Create"}
          cancelText="Cancel"
        >
          <Form form={form} layout="vertical">
            <Form.Item
              name="title"
              label="Email Title"
              rules={[
                { required: true, message: "Please input email title!" },
                { min: 2, message: "Email title must be at least 2 characters!" },
                { max: 150, message: "Email title cannot exceed 150 characters!" },
              ]}
            >
              <Input placeholder="Enter email template title" />
            </Form.Item>

            <Form.Item
              name="subject"
              label="Subject"
              rules={[
                { required: true, message: "Please input email subject!" },
                { min: 2, message: "Subject must be at least 2 characters!" },
                { max: 200, message: "Subject cannot exceed 200 characters!" },
              ]}
            >
              <Input placeholder="Enter email subject" />
            </Form.Item>

            <Form.Item
              name="fromEmailId"
              label="From Email ID"
              rules={[
                { required: true, message: "Please input from email!" },
                { type: 'email', message: 'Please enter a valid email address!' },
              ]}
            >
              <Input placeholder="Enter from email address" />
            </Form.Item>

            <Form.Item
              name="ccEmailId"
              label="CC Email ID"
              rules={[
                { type: 'email', message: 'Please enter a valid email address!' },
              ]}
            >
              <Input placeholder="Enter CC email address (optional)" />
            </Form.Item>

            <Form.Item
              name="bccEmailId"
              label="BCC Email ID"
              rules={[
                { type: 'email', message: 'Please enter a valid email address!' },
              ]}
            >
              <Input placeholder="Enter BCC email address (optional)" />
            </Form.Item>

            <Form.Item
              name="msg"
              label="Email Message/Content"
              rules={[
                { required: true, message: "Please input message content!" },
                { min: 10, message: "Message must be at least 10 characters!" },
              ]}
            >
              <WYSIWYGEditor
                placeholder="Enter email content with rich formatting (HTML supported)"
                height="200px"
              />
            </Form.Item>

            <Form.Item
              label="Variables Used"
            >
              <Input.TextArea
                value={(() => {
                  const uniqueVariables = extractVariables(formValues.subject, formValues.msg);
                  return uniqueVariables.length > 0
                    ? uniqueVariables.join(', ')
                    : 'No variables found in subject or message content';
                })()}
                rows={2}
                disabled
                style={{ backgroundColor: '#f5f5f5', color: '#666' }}
              />
            </Form.Item>

            <Form.Item
              name="status"
              label="Status"
              valuePropName="checked"
            >
              <Switch
                checkedChildren="Active"
                unCheckedChildren="Inactive"
              />
            </Form.Item>
          </Form>
        </Modal>

        {/* View Modal */}
        <Modal
          title={
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <i className="ti ti-eye" />
              View Email Template
            </div>
          }
          open={isViewModalVisible}
          onCancel={() => {
            setIsViewModalVisible(false);
            setViewingTemplate(null);
          }}
          footer={[
            <Button key="close" onClick={() => setIsViewModalVisible(false)}>
              Close
            </Button>
          ]}
          width={700}
        >
          {viewingTemplate && (
            <div style={{ padding: "16px 0" }}>
              <div style={{ marginBottom: "16px" }}>
                <strong>Title:</strong>
                <div style={{
                  marginTop: "8px",
                  padding: "8px 12px",
                  backgroundColor: "#f5f5f5",
                  borderRadius: "4px"
                }}>
                  {viewingTemplate?.title || 'N/A'}
                </div>
              </div>

              <div style={{ marginBottom: "16px" }}>
                <strong>Email Content:</strong>
                <div style={{
                  marginTop: "8px",
                  padding: "12px",
                  backgroundColor: "#f5f5f5",
                  borderRadius: "4px",
                  minHeight: "120px",
                  maxHeight: "300px",
                  overflowY: "auto"
                }}
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(viewingTemplate?.msg || 'No content') }}
                />
              </div>

              <div style={{ marginBottom: "16px" }}>
                <strong>Variables Used:</strong>
                <div style={{
                  marginTop: "8px",
                  padding: "8px 12px",
                  backgroundColor: "#f0f8ff",
                  borderRadius: "4px",
                  fontSize: "12px",
                  color: "#666"
                }}>
                  {(() => {
                    const variables = extractVariables(viewingTemplate?.subject || '', viewingTemplate?.msg || '');
                    return variables.length > 0
                      ? variables.join(', ')
                      : 'No variables found';
                  })()}
                </div>
              </div>

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <strong>Status:</strong>
                  <Tag
                    color={viewingTemplate?.status ? "success" : "error"}
                    style={{ marginLeft: "8px" }}
                  >
                    {viewingTemplate?.status ? "Active" : "Inactive"}
                  </Tag>
                </div>

                {viewingTemplate?.created_at && (
                  <div style={{ fontSize: "12px", color: "#666" }}>
                    Created: {new Date(viewingTemplate.created_at).toLocaleDateString("en-IN")}
                  </div>
                )}
              </div>
            </div>
          )}
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal
          title={
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <i className="ti ti-alert-triangle" style={{ color: "#ff4d4f" }} />
              Confirm Delete
            </div>
          }
          open={isDeleteModalVisible}
          onOk={confirmDelete}
          onCancel={cancelDelete}
          confirmLoading={emailTemplatesState.loading === "pending" && emailTemplatesState.action === "delete"}
          okText="Yes, Delete"
          cancelText="Cancel"
          okType="danger"
          centered
        >
          <p>Are you sure you want to delete this email template?</p>
          <p style={{ color: "#666", fontSize: "14px" }}>This action cannot be undone.</p>
        </Modal>
      </div>

      <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
        <p className="mb-0">DMRC Admin Panel, All Rights Reserved.</p>
      </div>
    </div>
  );
};

export default EmailTemplates;
